/**
 * Jester Dice Component
 * Two separate animated dice: one for file (column), one for rank (row)
 * Based on requirements in PROJECT.md
 */

import React, { useState, useEffect } from 'react';
import type { GameState, Color, DiceRoll } from '@jester-chess/shared';
import type { ClientSocket } from '../websocket/ClientSocket';
import './JesterDice.css';

interface JesterDiceProps {
  gameState: GameState;
  yourColor: Color;
  socket: ClientSocket | null;
}

export const JesterDice: React.FC<JesterDiceProps> = ({
  gameState,
  yourColor,
  socket,
}) => {
  const [fileDieValue, setFileDieValue] = useState<number | null>(null);
  const [rankDieValue, setRankDieValue] = useState<number | null>(null);
  const [isRolling, setIsRolling] = useState(false);
  const [canRoll, setCanRoll] = useState(false);
  const [hasRolledThisTurn, setHasRolledThisTurn] = useState(false);

  // Reset dice state when turn changes OR when jesterMovePending becomes true for our turn
  // This ensures the roll button is available after making a chess move
  useEffect(() => {
    if (gameState.currentTurn === yourColor) {
      setHasRolledThisTurn(false);
      setFileDieValue(null);
      setRankDieValue(null);
    }
  }, [gameState.currentTurn, yourColor]);

  // Additional safeguard: reset hasRolledThisTurn when jesterMovePending, mustRollJesterToEscape,
  // or mustRollJesterForStalemate becomes true for our turn - this handles edge cases
  useEffect(() => {
    const jesterActionRequired = gameState.jesterMovePending || gameState.mustRollJesterToEscape || gameState.mustRollJesterForStalemate;
    if (jesterActionRequired && gameState.currentTurn === yourColor && !isRolling) {
      // Jester action is now required and it's our turn - ensure we can roll
      setHasRolledThisTurn(false);
    }
  }, [gameState.jesterMovePending, gameState.mustRollJesterToEscape, gameState.mustRollJesterForStalemate, gameState.currentTurn, yourColor, isRolling]);

  // Check if it's player's turn and jester is on board
  // During normal play, jester move is optional after a regular piece move
  // During forced escape (in check, no legal moves, has jester), jester move is mandatory
  // During stalemate with jester, jester move is mandatory
  useEffect(() => {
    const jester = yourColor === 'white' ? gameState.whiteJester : gameState.blackJester;
    const isYourTurn = gameState.currentTurn === yourColor;
    const gameActive = gameState.status === 'active';
    const jesterMovePending = gameState.jesterMovePending;
    const mustRollToEscape = gameState.mustRollJesterToEscape;
    const mustRollForStalemate = gameState.mustRollJesterForStalemate;
    
    // Check if jester truly exists (not null AND not undefined AND has a square)
    const jesterExists = jester != null && jester.square != null;
    
    // Can roll if:
    // - It's your turn
    // - Jester exists on board
    // - Either jester move is pending (after regular piece move) OR must roll to escape (mandatory) OR must roll for stalemate
    // - Not already rolling
    // - Haven't rolled this turn
    // - Game is active
    const shouldCanRoll = 
      isYourTurn && 
      jesterExists && 
      (jesterMovePending || mustRollToEscape || mustRollForStalemate) &&
      !isRolling && 
      !hasRolledThisTurn && 
      gameActive;
    
    // Debug logging for rare stuck state issue
    if (isYourTurn && jesterExists && gameActive && !shouldCanRoll) {
      console.log('[JesterDice] Roll button disabled despite having jester:', {
        isYourTurn,
        jesterExists,
        jesterMovePending,
        mustRollToEscape,
        mustRollForStalemate,
        isRolling,
        hasRolledThisTurn,
        gameActive,
        currentTurn: gameState.currentTurn,
        yourColor,
      });
    }
    
    setCanRoll(shouldCanRoll);
  }, [gameState, yourColor, isRolling, hasRolledThisTurn]);

  const rollDice = () => {
    if (!canRoll || isRolling || !socket) return;

    setIsRolling(true);
    setFileDieValue(null);
    setRankDieValue(null);

    // Animate both dice (slower for d8 tumble effect)
    const fileDieInterval = setInterval(() => {
      setFileDieValue(Math.floor(Math.random() * 8) + 1);
    }, 250);

    const rankDieInterval = setInterval(() => {
      setRankDieValue(Math.floor(Math.random() * 8) + 1);
    }, 250);

    // Stop animation after 2.5 seconds and get final values (slower roll)
    setTimeout(() => {
      clearInterval(fileDieInterval);
      clearInterval(rankDieInterval);

      const finalFileDie = Math.floor(Math.random() * 8) + 1;
      const finalRankDie = Math.floor(Math.random() * 8) + 1;

      setFileDieValue(finalFileDie);
      setRankDieValue(finalRankDie);
      setIsRolling(false);
      setHasRolledThisTurn(true);

      // Send dice roll to server
      const diceRoll: DiceRoll = {
        fileDie: finalFileDie,
        rankDie: finalRankDie,
        timestamp: Date.now(),
      };

      socket.sendJesterRoll(diceRoll);
    }, 2500);
  };

  const files = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  return (
    <div className="jester-dice-container">
      <h2>Jester Dice</h2>
      <div className="dice-wrapper">
        {/* File Die */}
        <div className="die-container">
          <label>File</label>
          <div className={`die file-die ${isRolling ? 'rolling' : ''}`}>
            {fileDieValue !== null ? (
              <div className="die-value">
                <span className="die-number">{fileDieValue}</span>
                <span className="die-label">{files[fileDieValue - 1]}</span>
              </div>
            ) : (
              <div className="die-placeholder">?</div>
            )}
          </div>
        </div>

        {/* Rank Die */}
        <div className="die-container">
          <label>Rank</label>
          <div className={`die rank-die ${isRolling ? 'rolling' : ''}`}>
            {rankDieValue !== null ? (
              <div className="die-value">
                <span className="die-number">{rankDieValue}</span>
              </div>
            ) : (
              <div className="die-placeholder">?</div>
            )}
          </div>
        </div>
      </div>

      {fileDieValue !== null && rankDieValue !== null && !isRolling && (
        <div className="dice-result">
          <p>
            Moving jester to: {files[fileDieValue - 1]}{rankDieValue}
          </p>
        </div>
      )}

      <div className="dice-actions">
        <button
          className="roll-button"
          onClick={rollDice}
          disabled={!canRoll || isRolling}
        >
          {isRolling ? 'Rolling...' : 
           (gameState.mustRollJesterToEscape && gameState.currentTurn === yourColor) ? 'Roll to Escape!' : 
           (gameState.mustRollJesterForStalemate && gameState.currentTurn === yourColor) ? 'Roll (Stalemate)' :
           'Roll Dice'}
        </button>
        
        {/* Skip button only shows when jester move is optional (not when forced to roll to escape or stalemate) */}
        {gameState.jesterMovePending && 
         gameState.currentTurn === yourColor && 
         !isRolling && 
         gameState.status === 'active' &&
         !gameState.mustRollJesterToEscape &&
         !gameState.mustRollJesterForStalemate && (
          <button
            className="skip-button"
            onClick={() => socket?.sendSkipJesterMove()}
          >
            Skip Jester Move
          </button>
        )}
        
        {/* Warning when forced to roll for check escape */}
        {gameState.mustRollJesterToEscape && gameState.currentTurn === yourColor && (
          <p className="escape-warning">⚠️ You must roll your jester to attempt escape!</p>
        )}
        
        {/* Warning when forced to roll for stalemate */}
        {gameState.mustRollJesterForStalemate && gameState.currentTurn === yourColor && (
          <p className="escape-warning">⚠️ Stalemate! You must roll your jester (may capture a piece and break stalemate)</p>
        )}
      </div>
    </div>
  );
};

